import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useGetCooperativeRegistrationApplicationsQuery } from "../../../store/auth/authApi";
import { Pagination } from "../../../components";

function formatDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

function TableSkeleton() {
  const cols = 8;
  return (
    <table className="min-w-full divide-y divide-gray-200 border-collapse">
      <thead className="bg-gray-50">
        <tr>
          {Array.from({ length: cols }).map((_, i) => (
            <th
              key={i}
              className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
            >
              <div className="h-3 bg-gray-200 rounded w-20 animate-pulse" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {Array.from({ length: 8 }).map((_, rowIndex) => (
          <tr key={rowIndex}>
            {Array.from({ length: cols }).map((_, colIndex) => (
              <td key={colIndex} className="px-4 py-3">
                <div className="h-4 bg-gray-100 rounded w-24 animate-pulse" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function RegistrationApplicationsList() {
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState("pending");

  const {
    data: response,
    isLoading,
    error,
  } = useGetCooperativeRegistrationApplicationsQuery({ page: currentPage, status });

  const nested = response?.data;
  const rows = Array.isArray(nested?.data) ? nested.data : Array.isArray(nested) ? nested : [];
  const totalPages = response?.lastPage ?? 1;
  const hasMorePages = response?.hasMorePages ?? false;
  const hasPrevPage = currentPage > 1;

  const handleStatusChange = (next) => {
    setStatus(next);
    setCurrentPage(1);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <p className="text-red-600">
            {error?.data?.message ||
              error?.message ||
              "Failed to load registration applications."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
        <h1 className="text-2xl font-bold text-[#45464e] mb-2">
          Get Started applications
        </h1>
        <p className="text-gray-500 text-sm mb-6">
          Pending cooperative registrations from the marketing site. Provision from the API or a
          future admin action when ready.
        </p>

        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A]"
            >
              <option value="pending">Pending</option>
              <option value="all">All</option>
              <option value="provisioned">Provisioned</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-100">
          {isLoading ? (
            <TableSkeleton />
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-gray-500 text-sm">
              No applications in this filter.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organisation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slots
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {row.organisation_name || "—"}
                      {row.company_id ? (
                        <div className="mt-1">
                          <Link
                            to={`/cooperatives/${row.company_id}`}
                            className="text-[#8B313A] text-xs font-normal hover:underline"
                          >
                            Open cooperative
                          </Link>
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                      {row.domain_slug || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.contact_full_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 break-all max-w-[12rem]">
                      {row.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {row.phone || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">
                      {row.member_slot_count != null ? row.member_slot_count : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                        {row.status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {rows.length > 0 && (
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              hasNextPage={hasMorePages}
              hasPrevPage={hasPrevPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
